import React, { useState, useEffect, useContext } from "react";
import { Link, Outlet } from "react-router-dom";
import { useFirestore } from "../firebase/FirestoreContext";
import { collection, getDocs, deleteDoc, doc, updateDoc, writeBatch } from "firebase/firestore";
import { EnvContext } from "../../context/EnvContext";
import config from "../../config";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Switch } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import { DndContext, closestCenter, useSensors, useSensor, PointerSensor } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableTableRow = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: isDragging ? "#f0f0f0" : "inherit",
    cursor: "grab",
  };
  return (
    <tr ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </tr>
  );
};

const Modals = () => {
  const environment = useContext(EnvContext);
  const db = useFirestore();
  const storage = getStorage();
  const [modals, setModals] = useState([]);
  const [loading, setLoading] = useState(true);
  const collectionPath = environment === "staging" ? config.enveironment.staging.collectionPath : config.enveironment.production.collectionPath;

  const fetchModals = async () => {
    const querySnapshot = await getDocs(collection(db, `${collectionPath}Modals/CustomModals/DataBase`));
    const modalList = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })).sort((a, b) => a.sorting - b.sorting);
    setModals(modalList);
    setLoading(false);
  };

  useEffect(() => {
    fetchModals();
  }, [db]);

  const handleToggleActive = async (modalId, newStatus) => {
    setModals((prev) => prev.map((modal) => (modal.id === modalId ? { ...modal, active: newStatus } : modal)));
    try {
      const modalRef = doc(db, `${collectionPath}Modals/CustomModals/DataBase`, modalId);
      await updateDoc(modalRef, { active: newStatus });
      toast.success(`Modal ${newStatus ? "activated" : "deactivated"} successfully`);
    } catch (error) {
      console.error("Error updating modal status:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this modal?")) {
      await deleteDoc(doc(db, `${collectionPath}Modals/CustomModals/DataBase`, id));
      setModals(modals.filter((modal) => modal.id !== id));
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = modals.findIndex((modal) => modal.id === active.id);
      const newIndex = modals.findIndex((modal) => modal.id === over.id);
      const reordered = arrayMove(modals, oldIndex, newIndex);
      setModals(reordered);

      try {
        const batch = writeBatch(db);
        reordered.forEach((modal, index) => {
          const docRef = doc(db, `${collectionPath}Modals/CustomModals/DataBase`, modal.id);
          batch.update(docRef, { sorting: index + 1 });
        });
        await batch.commit();
        console.log("Sorting order successfully updated in Firestore.");
      } catch (error) {
        console.error("Error updating sorting order:", error);
      }
    }
  };

  return (
    <div style={styles.container}>
      <ToastContainer position="top-center" autoClose={5000} hideProgressBar={false} />
      <div style={styles.header}>
        <h2>Custom Modals</h2>
        <Link to="/main/modals/new">
          <button style={styles.button}>New Modal</button>
        </Link>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd} sensors={sensors}>
          <SortableContext items={modals.map((modal) => modal.id)} strategy={verticalListSortingStrategy}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.tableHeader}>Position</th>
                  <th style={styles.tableHeader}>Title</th>
                  <th style={styles.tableHeader}>Content</th>
                  <th style={styles.tableHeader}>Target</th>
                  <th style={styles.tableHeader}>Modal Image</th>
                  <th style={styles.tableHeader}>Active</th>
                  <th style={styles.tableHeader}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {modals.map((modal, index) => (
                  <SortableTableRow key={modal.id} id={modal.id}>
                    <td style={styles.tableCell}>{index + 1}</td>
                    <td style={styles.tableCell}>{modal.title}</td>
                    <td style={styles.tableCell}>{modal.content}</td>
                    <td style={styles.tableCell}>{modal.actionButtonText}</td>
                    <td style={styles.tableCell}>
                      {modal.imageUrl ? (
                        <div style={styles.imageContainer}>
                          <img src={modal.imageUrl} alt="Modal" style={styles.thumbnail} />
                        </div>
                      ) : (
                        <span style={{ color: "silver" }}>No image</span>
                      )}
                    </td>
                    <td style={styles.tableCell}>
                      <Switch checked={modal.active || false} onChange={(e) => handleToggleActive(modal.id, e.target.checked)} color="primary" />
                    </td>

                    <td style={styles.tableCell}>
                      <Link to={`/main/modals/edit/${modal.id}`}>
                        <button style={styles.smallButton}>Edit</button>
                      </Link>
                      <button style={styles.smallButton} onClick={() => handleDelete(modal.id)}>
                        Delete
                      </button>
                    </td>
                  </SortableTableRow>
                ))}
              </tbody>
            </table>
          </SortableContext>
        </DndContext>
      )}
      <Outlet context={{ fetchModals }} />
    </div>
  );
};

const styles = {
  container: { margin: "0 auto", padding: "20px", borderRadius: "8px" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" },
  button: { backgroundColor: "black", color: "white", border: "none", padding: "10px 15px", cursor: "pointer", borderRadius: "4px" },
  tableContainer: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse" },
  tableHeader: { textAlign: "left", padding: "10px", backgroundColor: "#f1f1f1", fontWeight: "bold", borderBottom: "1px solid #ccc" },
  tableRow: { borderBottom: "1px solid #ccc" },
  tableCell: { padding: "10px", textAlign: "left" },
  link: { color: "blue", textDecoration: "underline" },
  smallButton: { backgroundColor: "gray", color: "white", border: "none", padding: "5px 10px", margin: "0 5px", cursor: "pointer", borderRadius: "4px" },
  imageContainer: { display: "flex", alignItems: "center", gap: "10px" },
  thumbnail: { width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px" },
};

export default Modals;
